import {
  MOCK_PAMM_KPIS,
  MOCK_PAMM_FUNDS,
  MOCK_PAMM_INVESTORS,
  MOCK_INVESTMENT_REQUESTS,
  MOCK_PAMM_DEPOSITS,
  MOCK_PAMM_WITHDRAWALS,
  MOCK_PROFIT_DISTRIBUTIONS,
  MOCK_PAMM_TRADES,
  MOCK_ALLOCATION_HISTORY,
  MOCK_FEE_RECORDS,
  MOCK_PAMM_REPORTS,
  MOCK_PAMM_STATEMENTS,
  MOCK_PAMM_NOTIFICATIONS,
  MOCK_AUDIT_LOGS,
  MOCK_PAMM_SETTINGS,
  MOCK_FUND_PERFORMANCE,
  MOCK_INVESTOR_GROWTH,
  MOCK_RECENT_ACTIVITY,
} from '../mock';

import type {
  PAMMFund,
  PAMMInvestor,
  InvestmentRequest,
  PAMMDeposit,
  PAMMWithdrawal,
  ProfitDistribution,
  PAMMTrade,
  AllocationRecord,
  FeeRecord,
  PAMMReport,
  PAMMStatement,
  PAMMNotification,
  AuditLog,
  PAMMSettings,
  PAMMDashboardKPIs,
  FundPerformance,
  ReportType,
} from '../types';
const delay = (ms = 600) => new Promise(resolve => setTimeout(resolve, ms));

export const pammDashboardService = {
  async getKPIs(): Promise<PAMMDashboardKPIs> {
    await delay();
    return MOCK_PAMM_KPIS;
  },
  async getRecentActivity() {
    await delay();
    return MOCK_RECENT_ACTIVITY;
  },
  async getInvestorGrowth() {
    await delay();
    return MOCK_INVESTOR_GROWTH;
  },
};

export const pammFundService = {
  async getAll(): Promise<PAMMFund[]> {
    await delay();
    return MOCK_PAMM_FUNDS;
  },
  async getById(id: string): Promise<PAMMFund | undefined> {
    await delay();
    return MOCK_PAMM_FUNDS.find(f => f.id === id);
  },
  async create(data: Partial<PAMMFund>): Promise<PAMMFund> {
    await delay();
    const newFund: PAMMFund = {
      id: `fund-${Date.now()}`,
      name: data.name || 'New Fund',
      description: data.description || '',
      managerId: data.managerId || 'mgr-001',
      managerName: data.managerName || 'Unknown Manager',
      status: 'pending',
      currency: data.currency || 'USD',
      totalAUM: 0,
      minimumInvestment: data.minimumInvestment || 1000,
      maximumInvestment: data.maximumInvestment || 100000,
      allocationMethod: data.allocationMethod || 'percentage',
      performanceFee: data.performanceFee || 20,
      managementFee: data.managementFee || 2,
      entryFee: data.entryFee || 0,
      exitFee: data.exitFee || 1,
      highWaterMark: 0,
      totalReturn: 0,
      monthlyReturn: 0,
      drawdown: 0,
      sharpeRatio: 0,
      totalInvestors: 0,
      totalTrades: 0,
      winRate: 0,
      riskLevel: data.riskLevel || 'medium',
      leverage: data.leverage || '1:100',
      tradingSymbols: data.tradingSymbols || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      lockPeriodDays: data.lockPeriodDays || 30,
    };
    return newFund;
  },
  async update(id: string, data: Partial<PAMMFund>): Promise<PAMMFund> {
    await delay();
    const existing = MOCK_PAMM_FUNDS.find(f => f.id === id);
    if (!existing) throw new Error('Fund not found');
    return { ...existing, ...data, updatedAt: new Date().toISOString() };
  },
  async getPerformance(fundId: string): Promise<FundPerformance> {
    await delay();
    return { ...MOCK_FUND_PERFORMANCE, fundId };
  },
};

export const pammInvestorService = {
  async getAll(): Promise<PAMMInvestor[]> {
    await delay();
    return MOCK_PAMM_INVESTORS;
  },
  async getById(id: string): Promise<PAMMInvestor | undefined> {
    await delay();
    return MOCK_PAMM_INVESTORS.find(i => i.id === id);
  },
  async getByFund(fundId: string): Promise<PAMMInvestor[]> {
    await delay();
    return MOCK_PAMM_INVESTORS.filter(i => i.fundId === fundId);
  },
};

export const pammRequestService = {
  async getAll(): Promise<InvestmentRequest[]> {
    await delay();
    return MOCK_INVESTMENT_REQUESTS;
  },
  async approve(id: string): Promise<InvestmentRequest> {
    await delay();
    const req = MOCK_INVESTMENT_REQUESTS.find(r => r.id === id);
    if (!req) throw new Error('Request not found');
    return {
      ...req,
      status: 'approved',
      processedDate: new Date().toISOString(),
    };
  },
  async reject(id: string, reason?: string): Promise<InvestmentRequest> {
    await delay();
    const req = MOCK_INVESTMENT_REQUESTS.find(r => r.id === id);
    if (!req) throw new Error('Request not found');
    return {
      ...req,
      status: 'rejected',
      notes: reason,
      processedDate: new Date().toISOString(),
    };
  },
};

export const pammDepositService = {
  async getAll(): Promise<PAMMDeposit[]> {
    await delay();
    return MOCK_PAMM_DEPOSITS;
  },
  async approve(id: string): Promise<PAMMDeposit> {
    await delay();
    const dep = MOCK_PAMM_DEPOSITS.find(d => d.id === id);
    if (!dep) throw new Error('Deposit not found');
    return {
      ...dep,
      status: 'completed',
      processedDate: new Date().toISOString(),
    };
  },
  async reject(id: string, notes?: string): Promise<PAMMDeposit> {
    await delay();
    const dep = MOCK_PAMM_DEPOSITS.find(d => d.id === id);
    if (!dep) throw new Error('Deposit not found');
    return {
      ...dep,
      status: 'rejected',
      notes,
      processedDate: new Date().toISOString(),
    };
  },
};

export const pammWithdrawalService = {
  async getAll(): Promise<PAMMWithdrawal[]> {
    await delay();
    return MOCK_PAMM_WITHDRAWALS;
  },
  async approve(id: string): Promise<PAMMWithdrawal> {
    await delay();
    const wdr = MOCK_PAMM_WITHDRAWALS.find(w => w.id === id);
    if (!wdr) throw new Error('Withdrawal not found');
    return {
      ...wdr,
      status: 'completed',
      processedDate: new Date().toISOString(),
    };
  },
  async reject(id: string, notes?: string): Promise<PAMMWithdrawal> {
    await delay();
    const wdr = MOCK_PAMM_WITHDRAWALS.find(w => w.id === id);
    if (!wdr) throw new Error('Withdrawal not found');
    return {
      ...wdr,
      status: 'rejected',
      notes,
      processedDate: new Date().toISOString(),
    };
  },
};

export const pammDistributionService = {
  async getAll(): Promise<ProfitDistribution[]> {
    await delay();
    return MOCK_PROFIT_DISTRIBUTIONS;
  },
  async distribute(id: string): Promise<ProfitDistribution> {
    await delay();
    const dist = MOCK_PROFIT_DISTRIBUTIONS.find(d => d.id === id);
    if (!dist) throw new Error('Distribution not found');
    return {
      ...dist,
      status: 'distributed',
      distributedAt: new Date().toISOString(),
    };
  },
};

export const pammTradeService = {
  async getOpen(): Promise<PAMMTrade[]> {
    await delay();
    return MOCK_PAMM_TRADES.filter(t => t.status === 'open');
  },
  async getClosed(): Promise<PAMMTrade[]> {
    await delay();
    return MOCK_PAMM_TRADES.filter(t => t.status === 'closed');
  },
  async getAll(): Promise<PAMMTrade[]> {
    await delay();
    return MOCK_PAMM_TRADES;
  },
  async getByFund(fundId: string): Promise<PAMMTrade[]> {
    await delay();
    return MOCK_PAMM_TRADES.filter(t => t.fundId === fundId);
  },
  async close(id: string): Promise<PAMMTrade> {
    await delay();
    const trade = MOCK_PAMM_TRADES.find(t => t.id === id);
    if (!trade) throw new Error('Trade not found');
    return {
      ...trade,
      status: 'closed',
      closeTime: new Date().toISOString(),
      closePrice: trade.currentPrice,
    };
  },
};

export const pammAllocationService = {
  async getAll(): Promise<AllocationRecord[]> {
    await delay();
    return MOCK_ALLOCATION_HISTORY;
  },
  async getByFund(fundId: string): Promise<AllocationRecord[]> {
    await delay();
    return MOCK_ALLOCATION_HISTORY.filter(a => a.fundId === fundId);
  },
};

export const pammFeeService = {
  async getAll(): Promise<FeeRecord[]> {
    await delay();
    return MOCK_FEE_RECORDS;
  },
};

export const pammReportService = {
  async getAll(): Promise<PAMMReport[]> {
    await delay();
    return MOCK_PAMM_REPORTS;
  },
  async generate(
    fundId: string,
    type: string,
    period: string
  ): Promise<PAMMReport> {
    await delay(1200);
    return {
      id: `rpt-${Date.now()}`,
      fundId,
      fundName: MOCK_PAMM_FUNDS.find(f => f.id === fundId)?.name || 'Fund',
      title: `${type} Report - ${period}`,
      type: type as ReportType,
      period,
      generatedAt: new Date().toISOString(),
      generatedBy: 'Admin User',
      status: 'ready',
      fileSize: '2.1 MB',
      downloadUrl: '#',
    };
  },
};

export const pammStatementService = {
  async getAll(): Promise<PAMMStatement[]> {
    await delay();
    return MOCK_PAMM_STATEMENTS;
  },
};

export const pammNotificationService = {
  async getAll(): Promise<PAMMNotification[]> {
    await delay();
    return MOCK_PAMM_NOTIFICATIONS;
  },
  async markRead(id: string): Promise<PAMMNotification> {
    await delay(300);
    const notif = MOCK_PAMM_NOTIFICATIONS.find(n => n.id === id);
    if (!notif) throw new Error('Notification not found');
    return { ...notif, isRead: true };
  },
  async markAllRead(): Promise<void> {
    await delay(300);
  },
};

export const pammSettingsService = {
  async get(): Promise<PAMMSettings> {
    await delay();
    return MOCK_PAMM_SETTINGS;
  },
  async update(data: Partial<PAMMSettings>): Promise<PAMMSettings> {
    await delay(800);
    return { ...MOCK_PAMM_SETTINGS, ...data };
  },
};

export const pammAuditService = {
  async getAll(): Promise<AuditLog[]> {
    await delay();
    return MOCK_AUDIT_LOGS;
  },
};
